import axios, { AxiosError, AxiosInstance } from 'axios';
import { store } from '../store';
import { clearAuth, refreshToken as refreshTokenAction } from '../store/slices/authSlice';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getClientAppGateHeaders } from '../utils/clientAppGate';

// Use the same domain as the frontend to ensure cookies work properly
const API_BASE_URL = getApiBaseUrl();

// Create axios instance
// Note: withCredentials is set dynamically in request interceptor based on auth type
// JWT auth: withCredentials = false (don't send cookies)
// SESSION auth: withCredentials = true (send cookies)
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Default to false, will be set dynamically based on auth type
});

let isRefreshing = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let lastRefreshTime = 0;
const MIN_REFRESH_INTERVAL = 5000; // 5 seconds minimum between refresh attempts

let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.token;
    const authType = state.auth.type;
    const isAuthenticated = state.auth.isAuthenticated;

    // CRITICAL FIX: Only send cookies (withCredentials) for SESSION auth, not JWT
    // This prevents session cookies from being sent with JWT requests
    if (authType === 'SESSION') {
      config.withCredentials = true; // Send cookies for session auth
      delete config.headers.Authorization; // Remove Authorization header for session auth
    } else if (authType === 'JWT') {
      config.withCredentials = false; // Don't send cookies for JWT auth
      // Only add Authorization header for JWT auth and if authenticated
      if (token && isAuthenticated) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (!token && isAuthenticated) {
        // If JWT auth but no token, this might be a race condition
        // Log it but don't fail - let the response interceptor handle 401
        console.warn('JWT auth type but no token available in request interceptor');
      }
    } else {
      // Default: don't send credentials if auth type is unknown
      config.withCredentials = false;
    }

    const gateHeaders = getClientAppGateHeaders();
    Object.keys(gateHeaders).forEach((key) => {
      config.headers[key] = gateHeaders[key];
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;
    
    console.log('Axios interceptor: Request failed:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      statusText: error.response?.statusText
    });
    
    if (!error.response) {
      return Promise.reject(error);
    }

    // Handle 402 Payment Required — subscription has lapsed
    if (error.response.status === 402) {
      const responseData = error.response.data as any;
      const statusCode: string = responseData?.status || '';
      console.warn('Axios interceptor: 402 Payment Required —', responseData?.message);

      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (statusCode === 'ADMIN_SUBSCRIPTION_REQUIRED') {
        window.dispatchEvent(new CustomEvent('admin-subscription-expired', {
          detail: { message: responseData?.message || 'Your admin subscription has expired.' }
        }));
        if (!path.includes('subscription-renew')) {
          window.location.replace('/admin/subscription-renew');
        }
      } else {
        window.dispatchEvent(new CustomEvent('subscription-expired', {
          detail: { message: responseData?.message || 'Your subscription has expired. Please renew to continue.' }
        }));
        if (!path.includes('renew-subscription')) {
          window.location.replace('/renew-subscription');
        }
      }
      return Promise.reject(error);
    }

    // If the error is not 401, reject immediately
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // If it's a refresh/validation request that failed, don't retry
    // Check for both retail and admin refresh endpoints
    const isRefreshEndpoint = originalRequest.url === '/auth/refresh-token' || 
                             originalRequest.url === '/admin/auth/refresh-token' ||
                             originalRequest.url === '/auth/validate-session';
    
    if (isRefreshEndpoint) {
      console.log('Axios interceptor: Refresh/validation request failed, clearing auth state. URL:', originalRequest.url, 'Status:', error.response.status);
      store.dispatch(clearAuth());
      window.dispatchEvent(new CustomEvent('auth-expired'));
      return Promise.reject(error);
    }

    // If request has already been retried, don't retry again
    if (originalRequest._retry) {
      console.log('Request already retried, not attempting refresh again');
      return Promise.reject(error);
    }

    // Check authentication state
    const state = store.getState();
    const isAuthenticated = state.auth.isAuthenticated;
    const authType = state.auth.type;
    
    if (!isAuthenticated) {
      console.log('Not authenticated, clearing auth state');
      store.dispatch(clearAuth());
      window.dispatchEvent(new CustomEvent('auth-expired'));
      return Promise.reject(error);
    }

    // Check if we've exceeded maximum refresh attempts
    const now = Date.now();
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.log('Maximum refresh attempts exceeded, clearing auth state');
      refreshAttempts = 0;
      store.dispatch(clearAuth());
      window.dispatchEvent(new CustomEvent('auth-expired'));
      return Promise.reject(error);
    }

    // Check if we're trying to refresh too frequently
    // If a refresh is in progress or just completed, queue this request instead of rejecting
    if (isRefreshing || (now - lastRefreshTime < MIN_REFRESH_INTERVAL && lastRefreshTime > 0)) {
      console.log('Refresh in progress or too recent, queuing request for retry after refresh completes...');
      // Queue this request to retry after refresh completes
      try {
        const token = await new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
          // Set a timeout to prevent indefinite waiting
          setTimeout(() => {
            const index = failedQueue.findIndex(p => p.reject === reject);
            if (index !== -1) {
              failedQueue.splice(index, 1);
              reject(new Error('Request timeout while waiting for token refresh'));
            }
          }, 10000); // 10 second timeout
        });
        // Only update Authorization header for JWT auth
        if (authType === 'JWT') {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        // Mark this request as refresh-triggered to prevent loops
        originalRequest._isRefreshTriggered = true;
        return axiosInstance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // Prevent cascading 401s by checking if this request was triggered by a refresh
    if (originalRequest._isRefreshTriggered) {
      console.log('Request was triggered by refresh, not retrying to prevent loop');
      return Promise.reject(error);
    }

    if (isRefreshing) {
      try {
        const token = await new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        // Only update Authorization header for JWT auth
        if (authType === 'JWT') {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        // Mark this request as refresh-triggered to prevent loops
        originalRequest._isRefreshTriggered = true;
        return axiosInstance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    originalRequest._retry = true;
    isRefreshing = true;
    refreshAttempts++;
    lastRefreshTime = now;

    try {
      console.log(`Attempting authentication refresh (attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}) for ${authType} auth`);
      
      // Import the refresh function
      const { refreshAuthentication } = await import('../store/slices/authSlice');
      
      // Dispatch unified refresh action
      const refreshResult = await store.dispatch(refreshAuthentication());
      
      if (refreshResult.meta.requestStatus === 'rejected') {
        throw new Error('Authentication refresh failed');
      }

      // Get updated state
      const newState = store.getState();
      const newToken = newState.auth.token;
      const newAuthType = newState.auth.type;

      if (!newToken) {
        throw new Error('No new token received');
      }

      console.log('Authentication refresh successful, retrying original request');

      // Reset refresh attempts on successful refresh
      refreshAttempts = 0;

      // Update Authorization header only for JWT auth
      if (newAuthType === 'JWT') {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      } else {
        // For session auth, remove Authorization header
        delete axiosInstance.defaults.headers.common['Authorization'];
        delete originalRequest.headers.Authorization;
      }

      // Mark the retry request to prevent cascading loops
      originalRequest._isRefreshTriggered = true;

      processQueue(null, newToken);
      return axiosInstance(originalRequest);
    } catch (err) {
      console.error('Authentication refresh failed:', err);
      processQueue(err, null);
      
      // If we've exhausted all attempts, clear auth state
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        console.log('All refresh attempts failed, clearing auth state');
        refreshAttempts = 0;
        store.dispatch(clearAuth());
        window.dispatchEvent(new CustomEvent('auth-expired'));
      }
      
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance; 